<script lang="ts">
	import Button from '$lib/components/ui/button/button.svelte';
	import { Instagram } from 'lucide-svelte';
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';
	import StaticInstagram from '$lib/components/home/Instagram.svelte';

	type InstagramPost = {
		id: string;
		permalink: string;
		media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
		media_url?: string; // Included for IMAGE and CAROUSEL_ALBUM
		thumbnail_url?: string; // Included for VIDEO
		caption?: string;
		timestamp: string;
	};

	// Client-side state
	let posts: InstagramPost[] = [];
	let error: string | undefined = undefined;
	let isLoading = true;
	let lastRefresh = '';

	// Your Instagram profile URL for the main links
	const profileUrl = 'https://www.instagram.com/tassietours_tasmania/';
	const profileHandle = '@tassietours_tasmania';

	// Function to fetch Instagram posts client-side
	async function fetchInstagramPosts() {
		if (!browser) return;
		
		try {
			isLoading = true;
			const response = await fetch('/instagram-feed.json');
			
			if (!response.ok) {
				const errorData = await response.json();
				error = errorData.error || `Failed to load Instagram feed (${response.status})`;
				posts = [];
			} else {
				const data = await response.json();
				posts = data.posts || [];
				error = undefined;
			}
		} catch (err) {
			error = `Error loading Instagram feed: ${err instanceof Error ? err.message : 'Unknown error'}`;
			posts = [];
		} finally {
			if (error) console.warn('[InstagramFeed]', error);
			isLoading = false;
			lastRefresh = new Date().toLocaleTimeString();
		}
	}

	onMount(() => {
		// Fetch Instagram posts on component mount (client-side only)
		fetchInstagramPosts();
	});

	// Get up to 9 posts to display
	function getDisplayPosts() {
		if (posts && posts.length > 0) {
			return posts.slice(0, 9);
		}
		return [];
	}
</script>

{#if !isLoading && (error || !posts || posts.length === 0)}
	<!-- Fall back to the static image grid if the live feed is unavailable -->
	<StaticInstagram />
{:else}
<section class="mx-auto flex max-w-7xl flex-col px-4 py-12 lg:py-24">
	<h2
		class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		Connect with us on Instagram
	</h2>

	{#if isLoading}
		<div class="pt-12 text-center">
			<div class="flex items-center justify-center py-8">
				<div class="animate-pulse">Loading Instagram feed...</div>
			</div>
		</div>
	{:else}
		<div class="mx-auto grid w-full grid-cols-2 gap-4 pt-12 lg:grid-cols-4 lg:grid-rows-2">
			{#each getDisplayPosts() as post, index (post.id + '-' + index)}
				<a
					href={post.permalink}
					target="_blank"
					rel="noopener noreferrer"
					class="group relative col-span-1 row-span-1 rounded-2xl"
					class:lg:col-span-2={index === 0}
					class:lg:row-span-2={index === 0}
					aria-label="View Instagram post: {post.caption
						? post.caption.substring(0, 70) + '...'
						: 'Instagram Post'}"
				>
					{#if (post.media_type === 'VIDEO' && post.thumbnail_url) || post.media_url}
						<img
							src={post.media_type === 'VIDEO' ? post.thumbnail_url : post.media_url}
							alt={post.caption
								? post.caption.substring(0, 100) + '...'
								: 'Instagram post from ' + profileHandle}
							class="aspect-square w-full rounded-2xl object-cover"
							loading="lazy"
							width="400"
							height="400"
						/>
					{:else}
						<div class="flex aspect-square w-full items-center justify-center rounded-2xl bg-muted">
							<Instagram class="h-12 w-12 opacity-20" />
						</div>
					{/if}
					<div
						class="absolute inset-0 rounded-2xl bg-foreground/0 mix-blend-multiply transition-all group-hover:bg-foreground/20"
					></div>
					<Instagram
						class="absolute bottom-2 right-2 text-background/80 {index === 0
							? 'h-4 w-4 lg:h-8 lg:w-8'
							: 'h-6 w-6'}"
					/>

					{#if post.media_type === 'VIDEO'}
						<div class="absolute left-2 top-2 rounded bg-black/30 p-1 text-background/80">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={index === 0 ? '20' : '16'}
								height={index === 0 ? '20' : '16'}
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="lucide lucide-video"
								><path d="m22 8-6 4 6 4V8Z" /><rect
									width="14"
									height="12"
									x="2"
									y="6"
									rx="2"
									ry="2"
								/></svg
							>
						</div>
					{/if}
				</a>
			{/each}
		</div>
	{/if}

	<div class="mt-4 flex items-center justify-between">
		<div>
			{#if lastRefresh}
				<p class="text-xs text-muted-foreground">
					Content from Instagram • Last updated: {lastRefresh}
				</p>
			{/if}
		</div>
		<Button class="py-0" variant="link" href={profileUrl} target="_blank">{profileHandle}</Button>
	</div>
</section>
{/if}